import { useState } from "react";
import {
  CAlert,
  CCard,
  CCardBody,
  CCardHeader,
  CNav,
  CNavItem,
  CNavLink,
  CTabContent,
  CTabPane,
} from "@coreui/react";

export const PlaygroundPage = () => {
  const [activeTab, setActiveTab] = useState("tab1");

  // Mock data for demonstration
  const mockResults = [
    {
      id: "result1",
      name: "MACD Strategy Results",
      profit: 1250.5,
      winRate: 0.67,
      apy: 0.25,
    },
    {
      id: "result2",
      name: "Bollinger Bands Results",
      profit: 850.25,
      winRate: 0.74,
      apy: 0.34,
    },
    {
      id: "result3",
      name: "Grid Trading Results",
      profit: -125.75,
      winRate: 0.45,
      apy: -0.05,
    },
  ];

  // Sort by profit (descending)
  const sortedResults = mockResults.sort((a, b) => b.profit - a.profit);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  return (
    <div>
      <p className="text-muted mb-4">
        Quick results viewer for backtest runs, sorted by performance
      </p>

      {sortedResults.length === 0 ? (
        <CAlert color="info">
          No results to display. Run some backtests first to see results here.
        </CAlert>
      ) : (
        <CCard>
          <CCardHeader>
            <CNav variant="tabs" role="tablist">
              {sortedResults.map((result, index) => (
                <CNavItem key={result.id}>
                  <CNavLink
                    active={activeTab === `tab${index + 1}`}
                    onClick={() => setActiveTab(`tab${index + 1}`)}
                    style={{ cursor: "pointer" }}
                  >
                    {result.name}
                  </CNavLink>
                </CNavItem>
              ))}
            </CNav>
          </CCardHeader>
          <CCardBody>
            <CTabContent>
              {sortedResults.map((result, index) => (
                <CTabPane
                  key={result.id}
                  role="tabpanel"
                  aria-labelledby={`tab${index + 1}-tab`}
                  visible={activeTab === `tab${index + 1}`}
                >
                  <div className="row">
                    <div className="col-md-4">
                      <div className="text-center">
                        <h2 className={result.profit >= 0 ? "text-success" : "text-danger"}>
                          {formatCurrency(result.profit)}
                        </h2>
                        <p className="text-muted">Total Profit</p>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="text-center">
                        <h2 className="text-info">{formatPercentage(result.winRate)}</h2>
                        <p className="text-muted">Win Rate</p>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="text-center">
                        <h2 className={result.apy >= 0 ? "text-success" : "text-danger"}>
                          {formatPercentage(result.apy)}
                        </h2>
                        <p className="text-muted">Annual Return</p>
                      </div>
                    </div>
                  </div>

                  <hr />

                  <div className="mt-4">
                    <h5>Strategy Details</h5>
                    <p>
                      <strong>Strategy Name:</strong> {result.name}
                    </p>
                    <p className="text-muted">
                      Detailed performance charts and position analysis would be displayed here.
                      This is a simplified view for the playground demonstration.
                    </p>

                    {result.profit >= 0 ? (
                      <CAlert color="success">✅ This strategy showed positive performance</CAlert>
                    ) : (
                      <CAlert color="warning">
                        ⚠️ This strategy showed negative performance - review parameters
                      </CAlert>
                    )}
                  </div>
                </CTabPane>
              ))}
            </CTabContent>
          </CCardBody>
        </CCard>
      )}
    </div>
  );
};
